import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { useMessages } from '../../components/Messages';
import { API_CONFIG, getApiUrl, apiFetch } from '../../config/api';
import { showApiError, parseApiError, getErrorMessage } from '../../utils/errorUtils';
import UOMSelect from '../../components/Form/UOMSelect';
import './Products.css';
import { formatAppDate } from '../../utils/dateUtils';

const ViewProduct = forwardRef(({ productId, onBack, editMode = false, createMode = false, onProductCreated, onSave, onCancel, onEdit }, ref) => {
  const { t } = useTranslation('products');
  const { t: tCommon } = useTranslation('common');
  const { showSuccess, showError } = useMessages();
  const [product, setProduct] = useState(null);
  const [editData, setEditData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [hasChanges, setHasChanges] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [debouncedCustomerSearch, setDebouncedCustomerSearch] = useState('');
  const [customerSearchText, setCustomerSearchText] = useState('');
  const [productTypes, setProductTypes] = useState([]);
  const [colors, setColors] = useState([]);
  const [boms, setBoms] = useState([]);
  const [loadingBoms, setLoadingBoms] = useState(false);

  useImperativeHandle(ref, () => ({
    handleDelete,
    handleSave
  }));

  // Debounce customer search — wait 300ms after typing stops
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedCustomerSearch(customerSearchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [customerSearchTerm]);

  // Fetch customers when debounced search changes
  useEffect(() => {
    fetchCustomers(debouncedCustomerSearch);
  }, [debouncedCustomerSearch]);

  useEffect(() => {
    fetchProductTypes();
    fetchColors();
    if (productId && !createMode) {
      fetchProduct();
      fetchBOMs();
    } else if (createMode) {
      const newProduct = {
        product_code: '',
        product_name: '',
        description: '',
        product_type_id: null,
        dimensions: '',
        weight: null,
        color_id: null,
        finishing: '',
        standard_price: null,
        minimum_price: null,
        cost_estimate: null,
        lead_time_days: 0,
        minimum_order_qty: 1,
        unit_of_measure: 'PCS',
        customer_id: null,
        is_active: true
      };
      setProduct(newProduct);
      setEditData(newProduct);
    }
  }, [productId, createMode]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        if (editMode || createMode) {
          handleCancel();
        } else {
          if (onBack) onBack();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [editMode, createMode, onBack]);

  const fetchCustomers = async (search = '') => {
    try {
      const params = new URLSearchParams({ page_size: '50' });
      if (search) params.set('search', search);
      const response = await apiFetch(getApiUrl(`/api/customers?${params}`));
      if (response.ok) {
        const data = await response.json();
        setCustomers(data.customers || data || []);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const fetchProductTypes = async () => {
    try {
      const response = await apiFetch(getApiUrl('/api/product-types/'));
      if (response.ok) {
        const data = await response.json();
        setProductTypes(data.product_types || data || []);
      }
    } catch (error) {
      console.error('Error fetching product types:', error);
    }
  };

  const fetchColors = async () => {
    try {
      const response = await apiFetch(getApiUrl('/api/colors'));
      if (response.ok) {
        const data = await response.json();
        setColors(data.colors || data || []);
      }
    } catch (error) {
      console.error('Error fetching colors:', error);
    }
  };

  const fetchBOMs = async () => {
    if (!productId || createMode) return;
    try {
      setLoadingBoms(true);
      const response = await apiFetch(getApiUrl(`/api/boms?product_id=${productId}`));
      if (response.ok) {
        const data = await response.json();
        setBoms(data.boms || data || []);
      }
    } catch (error) {
      console.error('Error fetching BOMs:', error);
      setBoms([]);
    } finally {
      setLoadingBoms(false);
    }
  };

  const fetchProduct = async () => {
    if (!productId || productId === 'undefined') {
      console.error('Invalid product ID:', productId);
      setError(t('view.error.loadingError'));
      return;
    }
    setLoading(true);
    try {
      const response = await apiFetch(getApiUrl(`/api/products/${productId}`));
      if (response.ok) {
        const data = await response.json();
        setProduct(data);
        setEditData(data);
        // Initialize customer search text if product has a customer
        if (data.customer_name) {
          setCustomerSearchText(data.customer_name);
        }
      } else {
        setError(t('view.error.fetchFailed'));
      }
    } catch (error) {
      console.error('Error fetching product:', error);
      setError(t('view.error.loadingError'));
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setEditData(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      setError(null);
      const url = createMode
        ? getApiUrl('/api/products')
        : getApiUrl(`/api/products/${productId}`);

      const method = createMode ? 'POST' : 'PUT';

      const response = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editData)
      });

      if (response.ok) {
        const result = await response.json();
        setHasChanges(false);

        showSuccess(
          createMode ? t('view.messages.productCreated') : t('view.messages.productUpdated'),
          createMode ? t('view.messages.productCreatedDesc') : t('view.messages.productUpdatedDesc')
        );

        if (createMode && result.product_id) {
          setProduct(result);
          setEditData(result);
          if (onProductCreated) {
            onProductCreated(result.product_id);
          }
        } else if (!createMode) {
          await fetchProduct();
          // For updates, pass the current product with product_id to onSave
          if (onSave) onSave({ ...editData, product_id: productId });
          return;
        }

        if (onSave && createMode) onSave(result);
      } else {
        const errorData = await response.json();
        const parsed = parseApiError(errorData);
        const errorMessage = getErrorMessage(parsed, tCommon);
        setError(errorMessage);
        showError(
          createMode ? t('view.messages.productCreateFailed') : t('view.messages.productUpdateFailed'),
          errorMessage
        );
      }
    } catch (error) {
      console.error('Error saving product:', error);
      const errorMessage = tCommon('errors.INTERNAL_SERVER_ERROR');
      setError(errorMessage);
      showError(
        createMode ? t('view.messages.productCreateFailed') : t('view.messages.productUpdateFailed'),
        errorMessage
      );
    }
  };

  const handleCancel = () => {
    if (createMode) {
      if (onCancel) onCancel();
    } else {
      setEditData(product);
      setHasChanges(false);
      if (onCancel) onCancel();
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(t('view.messages.deleteConfirmation'))) {
      return;
    }

    try {
      const response = await apiFetch(getApiUrl(`/api/products/${productId}`), {
        method: 'DELETE'
      });

      if (response.ok) {
        showSuccess(t('view.messages.productDeleted'), t('view.messages.productDeletedDesc'));
        if (onBack) onBack();
      } else {
        const parsed = await showApiError(response, showError, tCommon, t('view.messages.productDeleteFailed'));
        setError(getErrorMessage(parsed, tCommon));
      }
    } catch (error) {
      console.error('Error deleting product:', error);
      const errorMessage = tCommon('errors.INTERNAL_SERVER_ERROR');
      setError(errorMessage);
      showError(t('view.messages.productDeleteFailed'), errorMessage);
    }
  };

  const renderOverviewTab = () => (
    <div className="tab-content">
      <div className="form-section">
        <h3>{t('view.sections.productInformation')}</h3>
        <div className="form-grid">
          <div className="form-group">
            <label>{t('view.fields.productName')} *</label>
            <input
              type="text"
              value={editData.product_name || ''}
              onChange={(e) => handleFieldChange('product_name', e.target.value)}
              disabled={!editMode && !createMode}
              required
            />
          </div>
          <div className="form-group">
            <label>{t('view.fields.productCode')} *</label>
            <input
              type="text"
              value={editData.product_code || ''}
              onChange={(e) => handleFieldChange('product_code', e.target.value)}
              disabled={!editMode && !createMode}
              placeholder={t('view.placeholders.productCode')}
              required
            />
          </div>
          <div className="form-group">
            <label>{t('view.fields.productType')} *</label>
            <select
              value={editData.product_type_id || ''}
              onChange={(e) => {
                const value = e.target.value ? parseInt(e.target.value, 10) : null;
                handleFieldChange('product_type_id', value);
              }}
              disabled={!editMode && !createMode}
              required
            >
              <option value="" disabled hidden>{t('view.placeholders.selectType')}</option>
              {productTypes.map(type => (
                <option key={type.product_type_id} value={type.product_type_id}>
                  {type.type_name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>{t('view.fields.customer')} *</label>
            {(editMode || createMode) ? (
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  list="customer-list"
                  value={customerSearchText}
                  onChange={(e) => {
                    const value = e.target.value;
                    setCustomerSearchText(value);
                    setCustomerSearchTerm(value);
                    const match = customers.find(c => c.customer_name === value);
                    if (match) {
                      handleFieldChange('customer_id', match.customer_id || match.id);
                    } else if (!value) {
                      handleFieldChange('customer_id', null);
                    }
                  }}
                  placeholder={t('view.placeholders.selectCustomer')}
                  required
                />
                <datalist id="customer-list">
                  {customers.map(customer => (
                    <option key={customer.customer_id || customer.id} value={customer.customer_name} />
                  ))}
                </datalist>
              </div>
            ) : (
              <input
                type="text"
                value={(() => {
                  const c = customers.find(c => (c.customer_id || c.id) === editData.customer_id);
                  return c ? c.customer_name : editData.customer_name || '';
                })()}
                disabled
              />
            )}
          </div>
          <div className="form-group full-width">
            <label>{t('view.fields.description')}</label>
            <textarea
              value={editData.description || ''}
              onChange={(e) => handleFieldChange('description', e.target.value)}
              disabled={!editMode && !createMode}
              placeholder={t('view.placeholders.description')}
              rows="2"
            />
          </div>
        </div>
      </div>

      <div className="form-section">
        <h3>{t('view.sections.dimensionsWeight')}</h3>
        <div className="form-grid">
          <div className="form-group span-2">
            <label>{t('view.fields.dimensions')}</label>
            <input
              type="text"
              value={editData.dimensions || ''}
              onChange={(e) => handleFieldChange('dimensions', e.target.value)}
              disabled={!editMode && !createMode}
              placeholder={t('view.placeholders.dimensions')}
            />
          </div>
          <div className="form-group span-2">
            <label>{t('view.fields.weight')}</label>
            <input
              type="number"
              step="0.001"
              value={editData.weight || ''}
              onChange={(e) => handleFieldChange('weight', e.target.value ? Number(e.target.value) : null)}
              disabled={!editMode && !createMode}
              placeholder={t('view.placeholders.weight')}
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderBOMTab = () => (
    <div className="tab-content">
      <div className="form-section">
        <h3>{t('view.sections.billOfMaterials')}</h3>
        {loadingBoms ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
{t('bomManagement.loading')}
          </div>
        ) : boms.length === 0 ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
{t('view.bom.noBomsMessage')}
          </div>
        ) : (
          <div className="bom-list">
            {boms.map((bom) => (
              <div key={bom.bom_id} className="bom-card" style={{
                border: '1px solid var(--border)',
                borderRadius: '8px',
                padding: '1.5rem',
                marginBottom: '1rem',
                background: bom.is_default ? 'var(--bg-secondary)' : 'var(--bg-primary)'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                  <div>
                    <h4 style={{ margin: '0 0 0.5rem 0', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      {bom.bom_name}
                      {bom.is_default && (
                        <span style={{
                          fontSize: '0.75rem',
                          padding: '0.25rem 0.5rem',
                          background: 'var(--primary)',
                          color: 'white',
                          borderRadius: '4px'
                        }}>{t('view.bom.defaultLabel')}</span>
                      )}
                      <span className={`status-badge status-${bom.status === 'ACTIVE' ? 'success' : bom.status === 'DRAFT' ? 'warning' : 'error'}`}>
                        {bom.status}
                      </span>
                    </h4>
                    <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                      {t('view.bom.version')} {bom.bom_version} | {t('view.bom.id')} BOM-{String(bom.bom_id).padStart(4, '0')}
                    </p>
                    {bom.description && (
                      <p style={{ margin: '0.5rem 0 0 0', color: 'var(--text-secondary)' }}>{bom.description}</p>
                    )}
                  </div>
                </div>

                <div className="bom-stats" style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                  gap: '1rem',
                  padding: '1rem',
                  background: 'var(--bg-tertiary)',
                  borderRadius: '6px',
                  marginBottom: '1rem'
                }}>
                  <div>
                    <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{t('view.bom.totalCost')}</div>
                    <div style={{ fontSize: '1.1rem', fontWeight: 600 }}>
                      ${bom.total_cost ? parseFloat(bom.total_cost).toFixed(4) : '0.0000'}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{t('view.bom.yield')}</div>
                    <div style={{ fontSize: '1.1rem', fontWeight: 600 }}>{bom.yield_percentage}%</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{t('view.bom.setupTime')}</div>
                    <div style={{ fontSize: '1.1rem', fontWeight: 600 }}>{bom.setup_time_minutes} min</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{t('view.bom.cycleTime')}</div>
                    <div style={{ fontSize: '1.1rem', fontWeight: 600 }}>{bom.cycle_time_minutes} min</div>
                  </div>
                </div>

                {bom.effective_date && (
                  <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
{t('view.bom.effective')} {formatAppDate(bom.effective_date)}
                    {bom.expiry_date && ` - ${formatAppDate(bom.expiry_date)}`}
                  </div>
                )}

                {bom.inventory_items && bom.inventory_items.length > 0 && (
                  <div style={{ marginTop: '1rem' }}>
                    <h5 style={{ marginBottom: '0.5rem' }}>{t('view.bom.inventoryItems')} ({bom.inventory_items.length})</h5>
                    <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                      <table style={{ width: '100%', fontSize: '0.9rem' }}>
                        <thead>
                          <tr style={{ borderBottom: '1px solid var(--border)' }}>
                            <th style={{ textAlign: 'left', padding: '0.5rem' }}>{t('view.bom.headers.item')}</th>
                            <th style={{ textAlign: 'right', padding: '0.5rem' }}>{t('view.bom.headers.qtyUnit')}</th>
                            <th style={{ textAlign: 'right', padding: '0.5rem' }}>{t('view.bom.headers.wastePercent')}</th>
                            <th style={{ textAlign: 'right', padding: '0.5rem' }}>{t('view.bom.headers.unitCost')}</th>
                            <th style={{ textAlign: 'right', padding: '0.5rem' }}>{t('view.bom.headers.extendedCost')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {bom.inventory_items.map((item, idx) => (
                            <tr key={idx} style={{ borderBottom: '1px solid var(--border-light)' }}>
                              <td style={{ padding: '0.5rem' }}>
                                <strong>{item.item_code}</strong>
                                <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                                  {item.item_name}
                                </div>
                              </td>
                              <td style={{ textAlign: 'right', padding: '0.5rem' }}>
                                {parseFloat(item.quantity_per_unit).toFixed(3)} {item.unit_of_measure}
                              </td>
                              <td style={{ textAlign: 'right', padding: '0.5rem' }}>
                                {item.waste_percentage || 0}%
                              </td>
                              <td style={{ textAlign: 'right', padding: '0.5rem' }}>
                                ${item.unit_cost ? parseFloat(item.unit_cost).toFixed(4) : '0.0000'}
                              </td>
                              <td style={{ textAlign: 'right', padding: '0.5rem', fontWeight: 600 }}>
                                ${item.extended_cost ? parseFloat(item.extended_cost).toFixed(4) : '0.0000'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const renderProductionTab = () => (
    <div className="tab-content">
      <div className="form-section">
        <h3>{t('view.sections.materialProduction')}</h3>
        <div className="form-grid">
          <div className="form-group">
            <label>{t('view.fields.dimensions')}</label>
            <input
              type="text"
              value={editData.dimensions || ''}
              onChange={(e) => handleFieldChange('dimensions', e.target.value)}
              disabled={!editMode && !createMode}
              placeholder={t('view.placeholders.dimensionsExample')}
            />
          </div>
          <div className="form-group">
            <label>{t('view.fields.weight')}</label>
            <input
              type="number"
              step="0.001"
              value={editData.weight || ''}
              onChange={(e) => handleFieldChange('weight', e.target.value ? Number(e.target.value) : null)}
              disabled={!editMode && !createMode}
              placeholder={t('view.placeholders.weightKg')}
            />
          </div>
          <div className="form-group">
            <label>{t('view.fields.color')}</label>
            <select
              value={editData.color_id || ''}
              onChange={(e) => handleFieldChange('color_id', e.target.value ? Number(e.target.value) : null)}
              disabled={!editMode && !createMode}
            >
              <option value="" disabled hidden>{t('view.placeholders.noColorSelected')}</option>
              {colors.map(color => (
                <option key={color.color_id} value={color.color_id}>
                  {color.color_name} {color.pantone_code && `(${color.pantone_code})`}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>{t('view.fields.finishing')}</label>
            <input
              type="text"
              value={editData.finishing || ''}
              onChange={(e) => handleFieldChange('finishing', e.target.value)}
              disabled={!editMode && !createMode}
              placeholder={t('view.placeholders.finishing')}
            />
          </div>
          <div className="form-group">
            <label>{t('view.fields.leadTime')}</label>
            <input
              type="number"
              min="0"
              value={editData.lead_time_days || 0}
              onChange={(e) => handleFieldChange('lead_time_days', Number(e.target.value))}
              disabled={!editMode && !createMode}
            />
          </div>
        </div>
      </div>

      <div className="form-section">
        <h3>{t('view.sections.pricingOrder')}</h3>
        <div className="form-grid">
          <div className="form-group">
            <label>{t('view.fields.standardPrice')}</label>
            <input
              type="number"
              step="0.01"
              value={editData.standard_price || ''}
              onChange={(e) => handleFieldChange('standard_price', e.target.value ? Number(e.target.value) : null)}
              disabled={!editMode && !createMode}
            />
          </div>
          <div className="form-group">
            <label>{t('view.fields.minimumPrice')}</label>
            <input
              type="number"
              step="0.01"
              value={editData.minimum_price || ''}
              onChange={(e) => handleFieldChange('minimum_price', e.target.value ? Number(e.target.value) : null)}
              disabled={!editMode && !createMode}
            />
          </div>
          <div className="form-group">
            <label>{t('view.fields.costEstimate')}</label>
            <input
              type="number"
              step="0.01"
              value={editData.cost_estimate || ''}
              onChange={(e) => handleFieldChange('cost_estimate', e.target.value ? Number(e.target.value) : null)}
              disabled={!editMode && !createMode}
            />
          </div>
          <div className="form-group">
            <label>{t('view.fields.minimumOrderQuantity')}</label>
            <input
              type="number"
              min="1"
              value={editData.minimum_order_qty || 1}
              onChange={(e) => handleFieldChange('minimum_order_qty', Number(e.target.value))}
              disabled={!editMode && !createMode}
            />
          </div>

          <div className="form-group">
            <label>{t('view.fields.unitOfMeasure')}</label>
            <UOMSelect
              value={editData.unit_of_measure || 'PCS'}
              onChange={(value) => handleFieldChange('unit_of_measure', value)}
              disabled={!editMode && !createMode}
            />
          </div>
        </div>
      </div>
    </div>
  );


  const renderDetailsTab = () => (
    <div className="tab-content">
      <div className="form-section">
        <h3>{t('view.sections.additionalInformation')}</h3>
        <div className="form-grid">
          <div className="form-group full-width">
            <label>{t('view.fields.specifications')}</label>
            <textarea
              value={editData.specifications || ''}
              onChange={(e) => handleFieldChange('specifications', e.target.value)}
              disabled={!editMode && !createMode}
              placeholder={t('view.placeholders.specifications')}
              rows="3"
            />
          </div>
          <div className="form-group full-width">
            <label>{t('view.fields.comments')}</label>
            <textarea
              value={editData.comments || ''}
              onChange={(e) => handleFieldChange('comments', e.target.value)}
              disabled={!editMode && !createMode}
              placeholder={t('view.placeholders.comments')}
              rows="2"
            />
          </div>
          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={editData.is_active || false}
                onChange={(e) => handleFieldChange('is_active', e.target.checked)}
                disabled={!editMode && !createMode}
              />
              <span>{t('view.fields.isActive')}</span>
            </label>
          </div>
        </div>
      </div>
    </div>
  );


  if (loading) {
    return <div className="view-container"><div className="loading">{t('view.loading')}</div></div>;
  }

  if (error) {
    return <div className="view-container"><div className="error-message">{error}</div></div>;
  }

  if (!product && !createMode) {
    return <div className="view-container"><div className="loading">{t('view.titles.noProductFound')}</div></div>;
  }

  return (
    <div className="view-container">
      <div className="view-header">
        <div className="header-title">
          <button className="btn-back" onClick={onBack}>←</button>
          <h1>
{createMode ? t('view.titles.newProduct') : editData.product_name || t('view.titles.product')}
          </h1>
          {!createMode && editData.is_active !== undefined && (
            <span className={`status-badge ${editData.is_active ? 'status-success' : 'status-error'}`}>
{editData.is_active ? t('list.status.active') : t('list.status.inactive')}
            </span>
          )}
        </div>
        <div className="header-actions">
          {!createMode && !editMode && (
            <>
              <button className="btn-secondary" onClick={onEdit}>{t('view.buttons.edit')}</button>
            </>
          )}
          {(createMode || editMode) && (
            <>
              <button className="btn-secondary" onClick={handleCancel}>{t('view.buttons.cancel')}</button>
              <button className="btn-primary" onClick={handleSave}>
                {createMode ? t('view.buttons.create') : t('view.buttons.save')}
              </button>
            </>
          )}
        </div>
      </div>
      <div className="tabs">
        <div className="tab-list">
          <button
            className={`tab-item ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
📋 {t('view.tabs.overview')}
          </button>
          <button
            className={`tab-item ${activeTab === 'bom' ? 'active' : ''}`}
            onClick={() => setActiveTab('bom')}
          >
🔧 {t('view.tabs.bom')}
          </button>
          <button
            className={`tab-item ${activeTab === 'production' ? 'active' : ''}`}
            onClick={() => setActiveTab('production')}
          >
🏭 {t('view.tabs.production')}
          </button>
          <button
            className={`tab-item ${activeTab === 'details' ? 'active' : ''}`}
            onClick={() => setActiveTab('details')}
          >
📐 {t('view.tabs.details')}
          </button>
        </div>

        <div className="tab-panels">
          {activeTab === 'overview' && renderOverviewTab()}
          {activeTab === 'bom' && renderBOMTab()}
          {activeTab === 'production' && renderProductionTab()}
          {activeTab === 'details' && renderDetailsTab()}
        </div>
      </div>
    </div>
  );
});

ViewProduct.displayName = 'ViewProduct';

export default ViewProduct;
